// AdminGymSalesPage.js — full monitoring page for the GymSales integration.
// Auto-refreshes every 15s. Master-admin only.
// Layout only — the meaty subcomponents live in /components/gymsales/*.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Server, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle, Zap,
  Activity, Clock, Search, PhoneCall, Info, ChevronDown, ChevronUp, ExternalLink,
  Key,
} from 'lucide-react';
import { API, StatusPill, fmtRel } from '../../components/gymsales/shared';
import { toast } from 'sonner';
import MatchRateSpark from '../../components/gymsales/MatchRateSpark';
import ProbeMatrix from '../../components/gymsales/ProbeMatrix';
import TenantConfigsCard from '../../components/gymsales/TenantConfigsCard';
import BackfillRunsCard from '../../components/gymsales/BackfillRunsCard';
import RouterMetricsCard from '../../components/gymsales/RouterMetricsCard';
import RouterPingCard from '../../components/gymsales/RouterPingCard';
import { MatchLogTable } from '../../components/gymsales/MatchLogTable';
import YabbitSmsAdminCard from '../../components/yabbit-sms/YabbitSmsAdminCard';

const HealthPill = ({ enabled, reachable }) => {
  if (!enabled) return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold bg-slate-500/20 text-slate-300 border-slate-500/40" data-testid="gymsales-health-disabled"><XCircle className="w-3.5 h-3.5" />Disabled</span>;
  if (reachable) return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold bg-emerald-500/20 text-emerald-300 border-emerald-500/40" data-testid="gymsales-health-reachable"><CheckCircle2 className="w-3.5 h-3.5" />Reachable</span>;
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold bg-red-500/20 text-red-300 border-red-500/40" data-testid="gymsales-health-unreachable"><XCircle className="w-3.5 h-3.5" />Unreachable</span>;
};

const AdminGymSalesPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [retesting, setRetesting] = useState(false);
  const [showRef, setShowRef] = useState(false);

  // Test lookup panel
  const [testPhone, setTestPhone] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [wdCfg, setWdCfg] = useState(null);
  const [wdSaving, setWdSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/gymsales/watchdog-config`).then((r) => setWdCfg(r.data)).catch(() => {});
  }, []);

  const saveWdCfg = async () => {
    setWdSaving(true);
    try {
      const r = await axios.put(`${API}/admin/gymsales/watchdog-config`, {
        fail_strikes: Number(wdCfg.fail_strikes), interval_min: Number(wdCfg.interval_min),
        down_interval_min: Number(wdCfg.down_interval_min),
      });
      setWdCfg(r.data);
      toast.success(`Watchdog tuned: ${r.data.fail_strikes} strike${r.data.fail_strikes === 1 ? '' : 's'} to flag down, checks every ${r.data.interval_min}m healthy / ${r.data.down_interval_min}m while down`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setWdSaving(false);
  };

  const zoomHour = async (ts) => {
    setZoom({ hour: ts, loading: true, probes: [] });
    try {
      const r = await axios.get(`${API}/admin/gymsales/uptime-hour?ts=${encodeURIComponent(ts)}`);
      setZoom({ ...r.data, loading: false });
    } catch {
      setZoom({ hour: ts, loading: false, probes: [], fails: 0 });
    }
  };
  const [testResult, setTestResult] = useState(null);
  const [testErr, setTestErr] = useState('');

  const timerRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/gymsales/diagnostics?hours=24`, { headers });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load diagnostics.');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load({ silent: true }), 15_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  const retest = useCallback(async () => {
    setRetesting(true);
    try {
      await axios.post(`${API}/admin/gymsales/retest`, {}, { headers });
      await load({ silent: true });
    } catch (e) { setErr(e?.response?.data?.detail || 'Re-test failed.'); }
    finally { setRetesting(false); }
  }, [headers, load]);

  const runTestLookup = useCallback(async () => {
    if (!testPhone.trim()) return;
    setTestBusy(true); setTestResult(null); setTestErr('');
    try {
      const r = await axios.post(`${API}/admin/gymsales/test-lookup`, { phone: testPhone.trim() }, { headers });
      setTestResult(r.data);
      await load({ silent: true });   // refresh recent-calls log
    } catch (e) { setTestErr(e?.response?.data?.detail || 'Lookup failed.'); }
    finally { setTestBusy(false); }
  }, [testPhone, headers, load]);

  if (loading && !data) {
    return <AdminLayout><div className="p-6 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div></AdminLayout>;
  }
  if (err && !data) {
    return <AdminLayout><div className="p-6 max-w-3xl mx-auto"><div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-200 flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}</div></div></AdminLayout>;
  }
  if (!data) return null;

  const { config, health, watchdog, endpoint_probes, rate_limit, recent_calls, stats, match_rate_trend, field_reference, rate_limit_docs } = data;
  const ratePct = Math.min(100, (rate_limit.spent / rate_limit.capacity) * 100);

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="gymsales-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Server className="w-6 h-6 text-emerald-400" />
              GymSales API monitoring
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Live status of the platform fallback (<span className="text-white/90 font-mono">{config.base_url}</span>). Auto-refreshes every 15s.
            </p>
          </div>
          <button
            onClick={retest}
            disabled={retesting}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded-lg"
            data-testid="gymsales-retest"
          >
            {retesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Re-test everything
          </button>
        </div>

        {/* Which tenants use GymSales? */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3 mb-4 flex items-start gap-2.5 text-xs text-white/70">
          <Info className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <div>
            <span className="text-white font-semibold">GymSales is an opt-in integration.</span>{' '}
            Only tenants who requested it during setup have credentials here. The panel below shows the <b>platform-level fallback</b> — a tenant without their own overrides would use it, but currently no tenant does. <b>TotalFusion</b> has its own connection at <span className="font-mono text-white/90">tfcore-gs-router.fly.dev/api/v1</span> — check its live health from <a href="/admin/domains" className="text-emerald-300 hover:text-emerald-200 underline">Domains &rarr; TotalFusion &rarr; Integrations</a> or masquerade in as the domain admin.
          </div>
        </div>

        {/* Health + Watchdog + Rate limit row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
            <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Health</div>
            <div className="flex items-center gap-2 mb-3"><HealthPill enabled={health.enabled} reachable={health.reachable} /></div>
            <div className="text-xs text-white/70 space-y-1">
              <div>Company: <b className="text-white">{health.company_id || '—'}</b></div>
              <div>Base URL: <span className="font-mono text-white/60 text-[11px]">{config.base_url}</span></div>
              <div>Auth user: <span className="font-mono text-white/60 text-[11px]">{config.api_username}</span></div>
              {health.error && <div className="text-rose-300">Error: {health.error}</div>}
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
            <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Availability watchdog</div>
            {watchdog ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  {watchdog.reachable
                    ? <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Up</span>
                    : <span className="inline-flex items-center gap-1 text-rose-300 text-xs font-semibold"><XCircle className="w-3.5 h-3.5" />Down</span>}
                </div>
                <div className="text-xs text-white/60 space-y-1">
                  <div>Checked <span className="text-white/80">{fmtRel(watchdog.checked_at)}</span></div>
                  {watchdog.error && <div className="text-rose-300">Last error: {watchdog.error}</div>}
                  <div className="text-white/40 italic text-[10px]">Alerts every 4h on flip. Checks every 10 min — every 2 min while down, so recovery is spotted fast.</div>
                </div>
              </>
            ) : (
              <div className="text-white/40 text-xs italic">Waiting for first watchdog tick…</div>
            )}
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
            <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2 flex items-center gap-1"><Zap className="w-3 h-3" />Rate limit — /people/*</div>
            <div className="text-2xl font-bold text-white tabular-nums">{rate_limit.spent}<span className="text-white/40 text-sm font-normal">/{rate_limit.capacity}</span></div>
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden my-2">
              <div
                className={`h-full rounded-full transition-all ${ratePct >= 90 ? 'bg-rose-500' : ratePct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${ratePct}%` }}
              />
            </div>
            <div className="text-[11px] text-white/50">
              Router allows {rate_limit.vendor_capacity_router ?? '—'}/min ·
              Direct API {rate_limit.vendor_capacity_direct ?? '—'}/min ·
              We stay under both at {rate_limit.capacity}/min
            </div>
          </div>
        </div>

        {/* 7-day uptime strip */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-4" data-testid="gymsales-uptime-strip">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2.5">
              <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold">7-day uptime — hourly</div>
              {(() => {
                const hist = data.uptime_history || [];
                const withData = hist.filter((h) => h.status !== 'nodata');
                if (!withData.length) return null;
                const down = withData.filter((h) => h.status === 'down').length;
                const pct = ((1 - down / withData.length) * 100);
                const txt = pct >= 99.95 ? '100%' : `${pct.toFixed(1)}%`;
                return (
                  <span className={`text-sm font-bold tabular-nums ${pct >= 99 ? 'text-emerald-300' : pct >= 95 ? 'text-amber-300' : 'text-rose-300'}`}
                    data-testid="gymsales-uptime-pct">
                    {txt} <span className="text-white/40 font-normal text-[11px]">this week</span>
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-3 text-[10px] text-white/50">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />Up</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" />Blip (recovered)</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500 inline-block" />Down</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-white/10 inline-block" />No data</span>
            </div>
          </div>
          {(data.uptime_history || []).some((h) => h.status !== 'nodata') ? (
            <div className="flex gap-[2px]" data-testid="gymsales-uptime-cells">
              {(data.uptime_history || []).map((h) => {
                const cls = h.status === 'up' ? 'bg-emerald-500' : h.status === 'blip' ? 'bg-amber-400' : h.status === 'down' ? 'bg-rose-500' : 'bg-white/10';
                const d = new Date(h.ts);
                return (
                  <div key={h.ts}
                    onClick={() => h.status !== 'nodata' && zoomHour(h.ts)}
                    className={`flex-1 h-6 rounded-[2px] ${cls} min-w-0 ${h.status !== 'nodata' ? 'cursor-pointer hover:opacity-70' : ''} ${zoom?.hour === h.ts ? 'ring-2 ring-white' : ''}`}
                    title={`${d.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })} — ${h.status === 'nodata' ? 'no data' : h.status}${h.ticks ? ` (${h.ticks} checks${h.fails ? `, ${h.fails} failed` : ''})` : ''} — click for details`} />
                );
              })}
            </div>
          ) : (
            <div className="text-white/40 text-xs italic">Collecting history — the watchdog records one point every 10 minutes from now on.</div>
          )}
          <div className="flex justify-between text-[10px] text-white/35 mt-1.5">
            <span>7 days ago</span><span>now</span>
          </div>
          {zoom && (
            <div className="mt-3 border border-white/10 rounded-lg bg-black/20 p-3" data-testid="uptime-hour-zoom">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-white/70 text-xs font-bold">
                  {new Date(zoom.hour).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} — {zoom.probes?.length || 0} probe{(zoom.probes?.length || 0) === 1 ? '' : 's'}, {zoom.fails || 0} failed
                </span>
                <button onClick={() => setZoom(null)} className="text-white/40 hover:text-white text-xs font-bold" data-testid="uptime-zoom-close">✕</button>
              </div>
              {zoom.loading ? (
                <span className="text-white/40 text-xs italic">Loading…</span>
              ) : (zoom.probes || []).length === 0 ? (
                <span className="text-white/40 text-xs italic">No probes recorded in this hour.</span>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {zoom.probes.map((p, i) => (
                    <div key={`${p.ts}-${i}`} className="flex items-start gap-2 text-xs" data-testid={`uptime-probe-${i}`}>
                      <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${p.probe_ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                      <span className="text-white/50 tabular-nums whitespace-nowrap">{new Date(p.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      {p.probe_ok ? (
                        <span className="text-white/60">OK{p.latency_ms ? ` · ${Math.round(p.latency_ms)}ms` : ''}</span>
                      ) : (
                        <span className="text-rose-300 break-all">
                          FAILED{!p.effective_reachable ? ' (counted as down)' : ' (blip — debounced)'}{p.error ? ` — ${p.error}` : ''}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-[10px] text-white/35 mt-1">A single failed probe shows as an amber blip and does not trigger an alert — tune the thresholds below.</p>
          <div className="flex items-end gap-3 flex-wrap mt-2 pt-2 border-t border-white/10" data-testid="watchdog-tuning">
            {[['fail_strikes', 'Strikes to flag down', 1, 5], ['interval_min', 'Healthy check (min)', 2, 60], ['down_interval_min', 'Down check (min)', 1, 30]].map(([k, label, mn, mx]) => (
              <label key={k} className="text-[10px] text-white/50 uppercase tracking-wide">
                {label}
                <input type="number" min={mn} max={mx}
                  value={wdCfg?.[k] ?? ''}
                  onChange={(e) => setWdCfg((c) => ({ ...c, [k]: e.target.value }))}
                  className="block mt-1 w-24 bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-white text-sm"
                  data-testid={`watchdog-cfg-${k}`} />
              </label>
            ))}
            <button onClick={saveWdCfg} disabled={wdSaving || !wdCfg}
              className="bg-teal-500 text-slate-950 font-bold text-xs px-3.5 py-2 rounded-lg hover:bg-teal-400 disabled:opacity-50"
              data-testid="watchdog-cfg-save">
              {wdSaving ? 'Saving…' : 'Save tuning'}
            </button>
          </div>
        </div>

        <MatchLogTable />

        {/* 24h summary + Match rate spark */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3 mb-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3">
              <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2 flex items-center gap-1"><Activity className="w-3 h-3" />Last {stats.window_hours}h</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div><div className="text-white/50">Total requests</div><div className="text-white text-lg font-bold tabular-nums">{stats.total}</div></div>
                <div><div className="text-white/50">Success rate</div><div className="text-white text-lg font-bold tabular-nums">{stats.success_rate == null ? '—' : `${Math.round(stats.success_rate * 100)}%`}</div></div>
                <div><div className="text-white/50">Errors</div><div className={`text-lg font-bold tabular-nums ${stats.errors > 0 ? 'text-rose-300' : 'text-white'}`}>{stats.errors}</div></div>
                <div><div className="text-white/50">People matched</div><div className="text-white text-lg font-bold tabular-nums">{stats.matched}</div></div>
                <div><div className="text-white/50">p95 latency</div><div className="text-white text-lg font-bold tabular-nums">{Math.round(stats.p95_ms)}<span className="text-white/40 text-sm font-normal">ms</span></div></div>
              </div>
            </div>
            <div className="lg:col-span-2 lg:border-l lg:border-white/10 lg:pl-4">
              <MatchRateSpark trend={match_rate_trend} />
            </div>
          </div>
        </div>

        <ProbeMatrix probes={endpoint_probes} isRetesting={retesting} />

        {/* Router Metrics Widget + Hourly Health Check */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4 mb-4">
          <RouterMetricsCard
            endpoint="/admin/gymsales/router-metrics"
            title="Router activity — all domains, last 24h"
          />
          <RouterPingCard
            endpoint="/admin/gymsales/router-pings"
            title="Router health check — hourly heartbeat"
            limit={48}
          />
        </div>

        {/* Test lookup + Recent calls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
            <div className="text-white font-semibold text-sm flex items-center gap-2 mb-2">
              <PhoneCall className="w-4 h-4 text-emerald-400" />Test phone lookup
            </div>
            <div className="flex gap-2 mb-3">
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+61415597332"
                className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-white placeholder-white/30 font-mono"
                data-testid="gymsales-test-phone-input"
              />
              <button
                type="button"
                onClick={runTestLookup}
                disabled={testBusy || !testPhone.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-semibold rounded"
                data-testid="gymsales-test-lookup-btn"
              >
                {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                Lookup
              </button>
            </div>
            {testErr && <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 mb-2">{testErr}</div>}
            {testResult && (
              <div className="space-y-2" data-testid="gymsales-test-result">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Quick lookup (ringing-popup shape)</div>
                <pre className="bg-black/30 border border-white/5 rounded p-2 text-[10px] text-emerald-200 overflow-x-auto max-h-32">
                  {JSON.stringify(testResult.quick_lookup, null, 2)}
                </pre>
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mt-2">Journey snapshot (shadow-grader shape)</div>
                {testResult.journey_error
                  ? <div className="text-rose-300 text-xs">{testResult.journey_error}</div>
                  : (
                    <pre className="bg-black/30 border border-white/5 rounded p-2 text-[10px] text-teal-200 overflow-x-auto max-h-48">
                      {JSON.stringify(testResult.journey_snapshot, null, 2)}
                    </pre>
                  )}
              </div>
            )}
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-semibold text-sm">
              <Clock className="w-4 h-4 text-emerald-400" />Recent API calls
              <span className="text-xs text-white/40 font-normal">(last 50, 7d TTL)</span>
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {recent_calls.length === 0 ? (
                <div className="text-white/40 text-xs italic p-4">No calls logged yet.</div>
              ) : (
                <table className="w-full text-xs" data-testid="gymsales-recent-calls">
                  <thead className="sticky top-0 bg-[#0F172A] text-white/50 text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-semibold">When</th>
                      <th className="text-left px-3 py-1.5 font-semibold">Endpoint</th>
                      <th className="text-center px-3 py-1.5 font-semibold">Code</th>
                      <th className="text-right px-3 py-1.5 font-semibold">ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_calls.map((r) => (
                      <tr key={r.id} className="border-t border-white/5" title={r.error || r.params_summary || ''}>
                        <td className="px-3 py-1.5 text-white/60 whitespace-nowrap">{fmtRel(r.created_at)}</td>
                        <td className="px-3 py-1.5 text-white/80 font-mono text-[10px]">
                          <div className="truncate max-w-[240px]">{r.path}</div>
                          {r.caller_context && <div className="text-white/40 text-[9px]">via {r.caller_context}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-center"><StatusPill code={r.status_code} ok={r.status_code >= 200 && r.status_code < 300} /></td>
                        <td className="px-3 py-1.5 text-right text-white/60 tabular-nums">{Math.round(r.duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Per-tenant credentials */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-semibold text-sm">
            <Key className="w-4 h-4 text-emerald-400" />
            Per-tenant credentials
            <span className="text-xs text-white/40 font-normal">— override the shared sandbox creds per fitness domain</span>
          </div>
          <TenantConfigsCard headers={headers} onSaved={() => load({ silent: true })} />
          <BackfillRunsCard headers={headers} />
        </div>

        {/* Yabbit SMS credentials — same shape as GymSales, YABBR gateway */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="yabbit-sms-moved-link">
          <p className="text-xs text-white/50">Yabbit SMS gateway credentials have moved to their own section.</p>
          <a href="/admin/yabbit-sms" className="text-xs font-semibold text-teal-300 hover:text-teal-100">Open Yabbit SMS →</a>
        </div>

        {/* Reference block */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowRef((s) => !s)}
            className="w-full px-4 py-3 flex items-center justify-between text-white font-semibold text-sm hover:bg-white/5"
            data-testid="gymsales-reference-toggle"
          >
            <span className="flex items-center gap-2"><Info className="w-4 h-4 text-emerald-400" />API reference — what we can query</span>
            {showRef ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
          </button>
          {showRef && (
            <div className="border-t border-white/10 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Endpoints we call</div>
                <ul className="space-y-1 text-white/80 font-mono text-[11px]">
                  {field_reference.endpoints_we_call.map((e) => (<li key={e}>{e}</li>))}
                </ul>
              </div>
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Rate limits</div>
                <ul className="space-y-1 text-white/70">
                  <li><b className="text-white">/people/*</b>: router {rate_limit_docs.people_endpoints.vendor_limit_gs_router_rpm}/min · direct {rate_limit_docs.people_endpoints.vendor_limit_direct_rpm}/min · we cap at {rate_limit_docs.people_endpoints.our_client_cap_rpm}/min</li>
                  <li><b className="text-white">Everything else</b>: router {rate_limit_docs.everything_else_rpm?.gs_router}/min · direct {rate_limit_docs.everything_else_rpm?.direct}/min</li>
                  <li>Router upstream timeout: {rate_limit_docs.upstream_timeout_seconds}s · our timeout: {rate_limit_docs.our_httpx_timeout_seconds}s</li>
                  <li className="text-white/50">{rate_limit_docs.people_endpoints.note}</li>
                </ul>
                <div className="mt-2 flex flex-col gap-1 text-[11px]">
                  <a href={rate_limit_docs.reference_direct} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                    Direct API docs<ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-white/40">Router reference: {rate_limit_docs.reference_router}</span>
                </div>
              </div>
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Person fields we read</div>
                <ul className="space-y-0.5 text-white/70 text-[11px]">
                  {field_reference.person_fields_we_read.map((f) => (<li key={f} className="font-mono">{f}</li>))}
                </ul>
              </div>
              <div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">Activity fields we read</div>
                <ul className="space-y-0.5 text-white/70 text-[11px]">
                  {field_reference.activity_fields_we_read.map((f) => (<li key={f} className="font-mono">{f}</li>))}
                </ul>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mt-3 mb-2">Note fields we read</div>
                <ul className="space-y-0.5 text-white/70 text-[11px]">
                  {field_reference.note_fields_we_read.map((f) => (<li key={f} className="font-mono">{f}</li>))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 text-[11px] text-white/40">
          Watchdog runs every 10 min. Availability flip emails master admins (throttled 4h). Audit log capped at 7-day TTL.
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGymSalesPage;
